import * as Sentry from "@sentry/browser";
import { SylaApiError } from "@syla/shared/errors/SylaApiError";
import { PopulatedWallet } from "@syla/shared/types/models/WalletBase";
import axios from "axios";
import React, { useState, useCallback, useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { importWalletCsv } from "../../../../store/actions/wallet";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import {
  FileImportMethod,
  getSupportedFileImportMethods,
} from "../helpers/getSupportedFileImportMethods";
import { isCompleted } from "../helpers/isCompleted";
import { FileImportPanel } from "../panels/FileImportPanel";
import { ImportMethodSelectionPanel } from "../panels/ImportMethodSelectionPanel";
import { ImportProgressPanel } from "../panels/ImportProgressPanel";

import { ImportStage } from "./ImportStage";

export type FileImportForm = {
  storedFiles: File[];
};

/**
 * A file Import panel that takes the user through the workflow of importing
 * a file to an existing data source.
 */
export function FileImportWorkflow({
  wallet,
  onClose,
  setAllowQuickClose,
}: {
  wallet: PopulatedWallet;
  onClose: () => void;
  setAllowQuickClose: (value: boolean) => void;
}) {
  const form = useForm<FileImportForm>({ defaultValues: { storedFiles: [] } });

  const workflow = useFileImportWorkflow(wallet);

  const customName = wallet.customName ?? wallet.dataSource.name;

  const handleImport = form.handleSubmit(async (values) => {
    const success = await workflow.beginImport(values.storedFiles);

    if (success) {
      form.reset({ storedFiles: [] });
    }
  });

  useEffect(() => {
    if (!setAllowQuickClose) {
      return;
    }

    if (
      workflow.state.stage === "selectImportMethod" ||
      isCompleted(workflow.state.stage)
    ) {
      setAllowQuickClose(true);
    } else {
      setAllowQuickClose(false);
    }
  }, [setAllowQuickClose, workflow.state.stage]);

  if (workflow.state.stage === "selectImportMethod") {
    return (
      <ImportMethodSelectionPanel
        dataSource={wallet.dataSource}
        importMethods={workflow.importMethods}
        customName={customName}
        onSelect={(method) => workflow.selectMethod(method as FileImportMethod)}
        onBack={onClose}
      />
    );
  }

  if (workflow.state.stage === "completingInput") {
    return (
      <FormProvider {...form}>
        <FileImportPanel
          wallet={wallet}
          customName={customName}
          fileImportMethod={workflow.state.importMethod}
          error={workflow.importError}
          clearError={() => workflow.setImportError(undefined)}
          onBack={() => {
            form.reset({ storedFiles: [] });
            workflow.backToSelectImportMethod();
            if (workflow.importMethods.length === 1) {
              onClose();
            }
          }}
          onImportStart={handleImport}
        />
      </FormProvider>
    );
  }

  // "importing" | "complete"
  return (
    <ImportProgressPanel
      importStage={workflow.state.stage}
      importMethod={workflow.state.importMethod}
      customName={customName}
      onComplete={onClose}
      dataSource={wallet.dataSource}
    />
  );
}

type FileImportStage = Extract<
  ImportStage,
  "selectImportMethod" | "completingInput" | "importing" | "complete"
>;

type FileImportState =
  | { stage: Extract<ImportStage, "selectImportMethod"> }
  | {
      stage: Extract<ImportStage, "completingInput" | "importing" | "complete">;
      importMethod: FileImportMethod;
    };

/**
 * Hook for managing a file import to an existing data source
 */
function useFileImportWorkflow(wallet: PopulatedWallet) {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);

  const importMethods = getSupportedFileImportMethods(wallet);

  if (importMethods.length === 0) {
    // shouldn't happen as custom file import is always expected to be available.
    throw new Error("No supported import methods");
  }

  // if there is only one import method available, then we can select it
  // immediately for the user
  const hasOnlyOneMethod = importMethods.length === 1;

  const [stage, setStage] = useState<FileImportStage>(
    hasOnlyOneMethod ? "completingInput" : "selectImportMethod",
  );

  const [importMethod, setImportMethod] = useState<
    FileImportMethod | undefined
  >(hasOnlyOneMethod ? importMethods[0] : undefined);

  const [importError, setImportError] = useState<
    Error | SylaApiError | unknown | undefined
  >();

  const selectMethod = useCallback((method: FileImportMethod) => {
    setStage("completingInput");
    setImportMethod(method);
  }, []);

  const backToSelectImportMethod = useCallback(() => {
    if (hasOnlyOneMethod) {
      return; // nothing to go back to, the caller should exit the flow
    }
    setStage("selectImportMethod");
  }, [hasOnlyOneMethod]);

  const beginImport = useCallback(
    async (files: File[]) => {
      if (stage !== "completingInput") {
        return false;
      }

      if (!files || files.length === 0) {
        throw new Error("Expected at least one file to import");
      }

      try {
        setImportError(undefined);
        setStage("importing");

        await importWalletCsv(accountId, wallet._id, files);

        setStage("complete");
        return true;
      } catch (error) {
        if (!axios.isAxiosError(error)) {
          Sentry.captureException(error);
        }

        setImportError(error);
        setStage("completingInput");

        return false;
      }
    },
    [accountId, stage, wallet],
  );

  return {
    state: {
      stage,
      importMethod,
    } as FileImportState,
    importMethods,
    importError,
    setImportError,
    selectMethod,
    backToSelectImportMethod,
    beginImport,
  };
}
